import React, {useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {
    GoogleMap,
    OVERLAY_MOUSE_TARGET,
    OverlayViewF,
    PolylineF,
    useGoogleMap,
} from '@react-google-maps/api';
import {BoxSeam, Check, Exclamation} from 'react-bootstrap-icons';
import {useGoogleMaps} from '../google_map/GoogleMapsContext';
import TruckTypeIcon from './TruckTypeIcon';
import {
    getDriverInitials,
    getDriverConnectionLabel,
    getDriverLocationLabel,
    getDriverTruckTypes,
    getOverlappingMarkerOffsets,
    getPickupLocations,
    getTrackingTarget,
    hasPickupDriver,
    isLocationFresh,
    isDriverOnline,
} from './dispatchUtils';

const WINNIPEG_CENTER = {lat: 49.8951, lng: -97.1384};
const WINNIPEG_DEFAULT_ZOOM = 12;
const MARKER_STACK_PEEK = 5;
const MAX_DIRECTIONS_ROUTE_POINTS = 27;

const getPlannedStopKey = (pickupId, kind, position) => (
    `${pickupId}:${kind}:${position.lat.toFixed(6)}:${position.lng.toFixed(6)}`
);

const mapOptions = {
    clickableIcons: false,
    disableDefaultUI: true,
    fullscreenControl: false,
    gestureHandling: 'greedy',
    mapTypeControl: false,
    streetViewControl: false,
    zoomControl: true,
    styles: [
        {featureType: 'poi', elementType: 'labels', stylers: [{visibility: 'off'}]},
        {featureType: 'transit', elementType: 'labels', stylers: [{visibility: 'off'}]},
        {featureType: 'road', elementType: 'geometry', stylers: [{color: '#ffffff'}]},
        {featureType: 'road', elementType: 'labels.text.fill', stylers: [{color: '#65748b'}]},
        {featureType: 'landscape', elementType: 'geometry', stylers: [{color: '#f3f6f9'}]},
        {featureType: 'water', elementType: 'geometry', stylers: [{color: '#cfe8f4'}]},
    ],
};

const driverOffset = () => ({x: -29, y: -26});
export const getDeliveryMarkerOffset = (width, height) => ({
    x: -(width / 2),
    y: -height,
});
const getMarkerSpreadStyle = (markerOffset = {}) => ({
    transform: `translate3d(${markerOffset.x || 0}px, ${markerOffset.y || 0}px, 0)`,
});
const getMarkerStackZIndex = (baseZIndex, markerOffset = {}) => (
    baseZIndex + ((markerOffset.x || 0) === 0 && (markerOffset.y || 0) === 0 ? 1 : 0)
);

export const focusMapOnDriver = (map, position) => {
    if (!map || !position) return false;

    map.panTo(position);
    return true;
};

export const getSelectedPickupRoutes = (pickups = [], selectedPickupIds = []) => {
    const selectedPickupIdSet = new Set(selectedPickupIds.map(String));

    return pickups.filter((pickup) => selectedPickupIdSet.has(String(pickup.id)))
        .map((pickup) => {
            const locations = getPickupLocations(pickup);
            return {
                pickupId: pickup.id,
                path: [...locations.origins, ...locations.dropoffs]
                    .map((location) => location.position),
            };
        })
        .filter((route) => route.path.length > 1);
};

const getRoutePositionKey = (position, precision = 5) => (
    `${Number(position.lat).toFixed(precision)}:${Number(position.lng).toFixed(precision)}`
);

export const getDirectionsRouteMetrics = (route, hasDriverStart, stopCount) => {
    const legs = route?.legs || [];
    const routeLegDurations = legs.map((leg) => Math.max(0, Math.ceil((leg.duration?.value || 0) / 60)));
    const stopLegDurations = hasDriverStart ? routeLegDurations : [0, ...routeLegDurations];
    const legDurationsMinutes = Array.from({length: stopCount}, (_, index) => stopLegDurations[index] || 0);

    return {
        source: 'roads',
        distanceKm: legs.reduce((total, leg) => total + (leg.distance?.value || 0), 0) / 1000,
        travelMinutes: routeLegDurations.reduce((total, minutes) => total + minutes, 0),
        legDurationsMinutes,
    };
};

export const getDirectionsForRequest = (directionsState, requestKey) => (
    directionsState?.requestKey === requestKey ? directionsState.directions : null
);

export const getSelectedPlannedRouteStops = (stops = [], selectedPickupIds = []) => {
    const selectedPickupIdSet = new Set(selectedPickupIds.map(String));

    return stops.filter((stop) => selectedPickupIdSet.has(String(stop.pickupId)));
};

const getStringHash = (value) => Array.from(String(value)).reduce(
    (hash, character) => ((hash * 31) + character.charCodeAt(0)) >>> 0,
    0
);

export const getDeliveryRouteColor = (pickupId) => {
    const hash = getStringHash(pickupId);
    const hue = (hash * 137.508) % 360;

    return `hsl(${hue.toFixed(1)} 70% 38%)`;
};

export const getPlannedRouteSegments = (startPosition, stops = []) => {
    const vertices = [
        ...(startPosition ? [{position: startPosition, stop: null}] : []),
        ...stops
            .filter((stop) => stop?.position)
            .map((stop) => ({position: stop.position, stop})),
    ];

    return vertices.slice(1).map((destination, index) => ({
        key: `${destination.stop?.key || destination.stop?.pickupId || index}:${index}`,
        pickupId: destination.stop?.pickupId,
        destinationStopKey: destination.stop?.key,
        kind: destination.stop?.kind,
        color: getDeliveryRouteColor(destination.stop?.pickupId),
        path: [vertices[index].position, destination.position],
        source: 'direct',
    })).filter((segment) => segment.pickupId !== null && segment.pickupId !== undefined);
};

const getDirectionsLegPath = (leg) => {
    const stepPath = (leg?.steps || []).flatMap((step) => (
        Array.isArray(step?.path) ? step.path : []
    ));

    if (stepPath.length > 1) return stepPath;
    return [leg?.start_location, leg?.end_location].filter(Boolean);
};

export const getDirectionsRouteSegments = (directions, startPosition, stops = []) => {
    const fallbackSegments = getPlannedRouteSegments(startPosition, stops);
    const legs = directions?.routes?.[0]?.legs || [];

    return fallbackSegments.map((segment, index) => {
        const roadPath = getDirectionsLegPath(legs[index]);
        return roadPath.length > 1
            ? {...segment, path: roadPath, source: 'roads'}
            : segment;
    });
};

export const detachRoutePolyline = (polyline) => {
    if (!polyline) return;

    polyline.setVisible?.(false);
    polyline.setPath?.([]);
    polyline.setMap?.(null);
    polyline.unbindAll?.();
    window.google?.maps?.event?.clearInstanceListeners?.(polyline);
};

export const detachDirectionsOverlay = (renderer) => {
    if (!renderer) return;

    renderer.setOptions?.({suppressPolylines: true, suppressMarkers: true});
    renderer.setPanel?.(null);
    renderer.setDirections?.(null);
    renderer.setMap?.(null);
    renderer.unbindAll?.();
    window.google?.maps?.event?.clearInstanceListeners?.(renderer);
};

const ManagedDirectionsOverlay = ({directions, routeKey, segments}) => {
    const map = useGoogleMap();

    useLayoutEffect(() => {
        if (!map || !directions || !window.google?.maps?.DirectionsRenderer) return undefined;

        const renderer = new window.google.maps.DirectionsRenderer({
            preserveViewport: true,
            suppressMarkers: true,
            suppressPolylines: true,
        });
        renderer.setMap(map);
        renderer.setDirections(directions);

        const polylines = window.google?.maps?.Polyline
            ? segments.filter((segment) => segment.path.length > 1).map((segment) => (
                new window.google.maps.Polyline({
                    map,
                    path: segment.path,
                    clickable: false,
                    strokeColor: segment.color,
                    strokeOpacity: segment.source === 'roads' ? 0.9 : 0.7,
                    strokeWeight: segment.source === 'roads' ? 5 : 4,
                    zIndex: 4,
                })
            ))
            : [];

        return () => {
            polylines.forEach(detachRoutePolyline);
            detachDirectionsOverlay(renderer);
        };
    }, [directions, map, routeKey, segments]);

    return null;
};

const PlannedRoadRoute = ({
    startPosition,
    stops,
    selectedDriverId,
    onRouteMetrics,
}) => {
    const [directionsState, setDirectionsState] = useState({
        requestKey: null,
        directions: null,
    });
    const directionsServiceRef = useRef(null);
    const routeCacheRef = useRef(new Map());
    const stopOrderKey = useMemo(
        () => stops.map((stop) => stop.key).join('|'),
        [stops]
    );
    const path = useMemo(() => [
        startPosition,
        ...stops.map((stop) => stop.position),
    ].filter(Boolean), [startPosition, stops]);
    const pathRef = useRef(path);
    const startPositionRef = useRef(startPosition);
    const stopCountRef = useRef(stops.length);
    pathRef.current = path;
    startPositionRef.current = startPosition;
    stopCountRef.current = stops.length;
    const requestKey = useMemo(() => [
        selectedDriverId || 'no-driver',
        stopOrderKey || 'no-stops',
        startPosition ? getRoutePositionKey(startPosition, 3) : 'no-gps',
        ...stops.map((stop) => getRoutePositionKey(stop.position)),
    ].join('|'), [selectedDriverId, startPosition, stopOrderKey, stops]);
    const directions = getDirectionsForRequest(directionsState, requestKey);
    const fallbackSegments = useMemo(
        () => getPlannedRouteSegments(startPosition, stops),
        [startPosition, stops]
    );
    const directionsSegments = useMemo(
        () => getDirectionsRouteSegments(directions, startPosition, stops),
        [directions, startPosition, stops]
    );

    useEffect(() => {
        let active = true;
        let requestTimer = null;
        const requestPath = pathRef.current;
        const hasDriverStart = Boolean(startPositionRef.current);
        const emptyLegDurations = Array.from({length: stopCountRef.current}, () => 0);
        const reportFallback = (reason = null) => {
            setDirectionsState({requestKey, directions: null});
            onRouteMetrics?.({
                stopOrderKey,
                source: 'direct',
                distanceKm: null,
                travelMinutes: 0,
                legDurationsMinutes: emptyLegDurations,
                reason,
            });
        };

        if (requestPath.length < 2) {
            reportFallback('not-enough-points');
            return undefined;
        }
        if (requestPath.length > MAX_DIRECTIONS_ROUTE_POINTS) {
            reportFallback('waypoint-limit');
            return undefined;
        }
        if (!window.google?.maps?.DirectionsService) {
            reportFallback('directions-unavailable');
            return undefined;
        }

        const cachedRoute = routeCacheRef.current.get(requestKey);
        if (cachedRoute) {
            setDirectionsState({requestKey, directions: cachedRoute.directions});
            onRouteMetrics?.({...cachedRoute.metrics, stopOrderKey});
            return undefined;
        }

        setDirectionsState({requestKey, directions: null});
        onRouteMetrics?.({
            stopOrderKey,
            source: 'loading',
            distanceKm: null,
            travelMinutes: 0,
            legDurationsMinutes: emptyLegDurations,
        });

        requestTimer = window.setTimeout(() => {
            if (!directionsServiceRef.current) {
                directionsServiceRef.current = new window.google.maps.DirectionsService();
            }

            const request = {
                origin: requestPath[0],
                destination: requestPath[requestPath.length - 1],
                waypoints: requestPath.slice(1, -1).map((position) => ({location: position, stopover: true})),
                travelMode: window.google.maps.TravelMode.DRIVING,
                optimizeWaypoints: false,
            };

            directionsServiceRef.current.route(request, (result, status) => {
                if (!active) return;
                if (status !== window.google.maps.DirectionsStatus.OK || !result?.routes?.[0]) {
                    reportFallback(status || 'directions-failed');
                    return;
                }

                const metrics = getDirectionsRouteMetrics(
                    result.routes[0],
                    hasDriverStart,
                    stopCountRef.current
                );

                if (routeCacheRef.current.size >= 25) {
                    routeCacheRef.current.delete(routeCacheRef.current.keys().next().value);
                }
                routeCacheRef.current.set(requestKey, {directions: result, metrics});
                setDirectionsState({requestKey, directions: result});
                onRouteMetrics?.({...metrics, stopOrderKey});
            });
        }, 500);

        return () => {
            active = false;
            if (requestTimer) window.clearTimeout(requestTimer);
        };
    }, [onRouteMetrics, requestKey, stopOrderKey]);

    if (directions) {
        return (
            <ManagedDirectionsOverlay
                directions={directions}
                routeKey={requestKey}
                segments={directionsSegments}
            />
        );
    }

    return fallbackSegments.map((segment) => (
        <PolylineF
            key={`planned-fallback-${segment.key}`}
            path={segment.path}
            options={{
                strokeColor: segment.color,
                strokeOpacity: 0.72,
                strokeWeight: 4,
                zIndex: 4,
            }}
        />
    ));
};

const FitMapBounds = ({
    points,
    request,
    ignoreZeroRequest = false,
    padding = {top: 78, right: 70, bottom: 70, left: 70},
}) => {
    const map = useGoogleMap();
    const pointsRef = useRef(points);
    const paddingRef = useRef(padding);
    pointsRef.current = points;
    // Panel resizing updates padding continuously. Keep the latest value for the
    // next requested fit without changing the user's current center or zoom.
    paddingRef.current = padding;

    useEffect(() => {
        const currentPoints = pointsRef.current;
        if (!map || !window.google || (ignoreZeroRequest && request === 0)) return;

        if (currentPoints.length === 0) {
            map.panTo(WINNIPEG_CENTER);
            map.setZoom(WINNIPEG_DEFAULT_ZOOM);
            return;
        }

        if (currentPoints.length === 1) {
            map.panTo(currentPoints[0]);
            map.setZoom(13);
            return;
        }

        const bounds = new window.google.maps.LatLngBounds();
        currentPoints.forEach((point) => bounds.extend(point));
        map.fitBounds(bounds, paddingRef.current);

        const listener = window.google.maps.event.addListenerOnce(map, 'idle', () => {
            if (map.getZoom() > 15) map.setZoom(15);
        });

        return () => window.google.maps.event.removeListener(listener);
    }, [ignoreZeroRequest, map, request]);

    return null;
};

const FocusDriver = ({position, request}) => {
    const map = useGoogleMap();
    const lastFocusedRequestRef = useRef(0);

    useEffect(() => {
        if (!map || !position || request === 0 || lastFocusedRequestRef.current === request) return;

        lastFocusedRequestRef.current = request;
        focusMapOnDriver(map, position);
    }, [map, position, request]);

    return null;
};

const DispatchMapCanvas = ({
    drivers,
    driverLocations,
    driverPresence = {},
    driverAvailabilityById = new Map(),
    pickups,
    selectedDriverId,
    selectedPickupIds = [],
    onSelectDriver,
    onSelectPickup,
    fitRequest,
    routeFitRequest = 0,
    driverFocusRequest,
    plannedRouteStops = [],
    onRouteMetrics,
    now,
    fitPadding,
}) => {
    const {isLoaded, loadError} = useGoogleMaps();

    const activeSelectedPickupIds = useMemo(() => Array.from(new Set(
        selectedPickupIds
            .filter((pickupId) => pickupId !== null && pickupId !== undefined)
            .map(String)
    )), [
        selectedPickupIds,
    ]);
    const selectedPickupIdSet = useMemo(
        () => new Set(activeSelectedPickupIds),
        [activeSelectedPickupIds]
    );
    const selectedPickups = useMemo(
        () => pickups.filter((pickup) => selectedPickupIdSet.has(String(pickup.id))),
        [pickups, selectedPickupIdSet]
    );
    const selectedPlannedRouteStops = useMemo(
        () => getSelectedPlannedRouteStops(plannedRouteStops, activeSelectedPickupIds),
        [activeSelectedPickupIds, plannedRouteStops]
    );
    const plannedStopSequenceByKey = useMemo(() => new Map(
        selectedPlannedRouteStops.map((stop) => [
            getPlannedStopKey(stop.pickupId, stop.kind, stop.position),
            stop.sequence,
        ])
    ), [selectedPlannedRouteStops]);
    const plannedPickupIdSet = useMemo(
        () => new Set(selectedPlannedRouteStops.map((stop) => String(stop.pickupId))),
        [selectedPlannedRouteStops]
    );

    const deliveryMarkers = useMemo(() => pickups.flatMap((pickup) => {
        const locations = getPickupLocations(pickup);
        const isSelected = selectedPickupIdSet.has(String(pickup.id));
        const isPlanned = plannedPickupIdSet.has(String(pickup.id));
        const originsToShow = isPlanned
            ? locations.origins.filter((location) => plannedStopSequenceByKey.has(
                getPlannedStopKey(pickup.id, 'origin', location.position)
            ))
            : isSelected ? locations.origins : locations.origins.slice(0, 1);
        const dropoffsToShow = isPlanned
            ? locations.dropoffs.filter((location) => plannedStopSequenceByKey.has(
                getPlannedStopKey(pickup.id, 'dropoff', location.position)
            ))
            : isSelected ? locations.dropoffs : [];
        const origins = originsToShow.map((location, index) => ({
            ...location,
            key: `pickup-${pickup.id}-origin-${location.id}-${index}`,
            pickup,
            kind: 'origin',
            label: plannedStopSequenceByKey.get(getPlannedStopKey(pickup.id, 'origin', location.position))
                ?? (isSelected && locations.origins.length > 1 ? `${index + 1}` : `#${pickup.id}`),
        }));
        const dropoffs = dropoffsToShow.map((location, index) => ({
            ...location,
            key: `pickup-${pickup.id}-dropoff-${location.id}-${index}`,
            pickup,
            kind: 'dropoff',
            label: plannedStopSequenceByKey.get(getPlannedStopKey(pickup.id, 'dropoff', location.position))
                ?? (locations.dropoffs.length > 1 ? `${index + 1}` : 'D'),
        }));

        return [...origins, ...dropoffs];
    }), [pickups, plannedPickupIdSet, plannedStopSequenceByKey, selectedPickupIdSet]);

    const routePaths = useMemo(
        () => getSelectedPickupRoutes(pickups, activeSelectedPickupIds),
        [activeSelectedPickupIds, pickups]
    );
    const selectedDriverLocation = selectedDriverId ? driverLocations[selectedDriverId] : null;
    const plannedRouteBoundsPoints = useMemo(() => [
        selectedDriverLocation,
        ...selectedPlannedRouteStops.map((stop) => stop.position),
    ].filter(Boolean), [selectedDriverLocation, selectedPlannedRouteStops]);
    const trackingPaths = useMemo(() => selectedPickups.map((pickup) => {
        const driverLocation = pickup.driver?.id ? driverLocations[pickup.driver.id] : null;
        const trackingTarget = getTrackingTarget(pickup);

        if (!driverLocation || !trackingTarget) return null;
        return {
            pickupId: pickup.id,
            path: [driverLocation, trackingTarget],
            color: getDeliveryRouteColor(pickup.id),
        };
    }).filter(Boolean), [driverLocations, selectedPickups]);

    const boundsPoints = useMemo(() => [
        ...drivers.map((driver) => driverLocations[driver.id]).filter(Boolean).map(({lat, lng}) => ({lat, lng})),
        ...deliveryMarkers.map((marker) => marker.position),
    ], [deliveryMarkers, driverLocations, drivers]);

    const mapMarkerItems = useMemo(() => [
        ...deliveryMarkers.map((marker) => ({
            ...marker,
            type: 'delivery',
            key: marker.key,
            position: marker.position,
            anchor: selectedPickupIdSet.has(String(marker.pickup.id)),
        })),
        ...drivers.flatMap((driver) => {
            const location = driverLocations[driver.id];
            if (!location) return [];

            return [{
                type: 'driver',
                key: `driver-${driver.id}`,
                position: location,
                location,
                driver,
                availability: driverAvailabilityById.get(String(driver.id)) || 'unavailable',
                anchor: String(driver.id) === String(selectedDriverId),
            }];
        }),
    ], [
        deliveryMarkers,
        driverAvailabilityById,
        driverLocations,
        drivers,
        selectedDriverId,
        selectedPickupIdSet,
    ]);
    const driverMarkerItems = useMemo(
        () => mapMarkerItems.filter((item) => item.type === 'driver'),
        [mapMarkerItems]
    );
    const deliveryMarkerItems = useMemo(
        () => mapMarkerItems.filter((item) => item.type === 'delivery'),
        [mapMarkerItems]
    );
    const markerOffsets = useMemo(
        () => getOverlappingMarkerOffsets(mapMarkerItems, MARKER_STACK_PEEK),
        [mapMarkerItems]
    );

    if (loadError) {
        return (
            <div className="dispatch-map-state" role="alert">
                <span className="dispatch-map-state-icon"><i className="fa fa-map-o"/></span>
                <h3>Map could not be loaded</h3>
                <p>Check the Google Maps key and try refreshing this page.</p>
            </div>
        );
    }

    if (!isLoaded) {
        return (
            <div className="dispatch-map-state dispatch-map-loading" aria-label="Loading dispatcher map">
                <span className="dispatch-map-spinner"/>
                <h3>Loading live map</h3>
                <p>Connecting drivers and deliveries...</p>
            </div>
        );
    }

    return (
        <GoogleMap
            mapContainerClassName="dispatch-google-map"
            center={WINNIPEG_CENTER}
            zoom={WINNIPEG_DEFAULT_ZOOM}
            options={mapOptions}
        >
            <FitMapBounds points={boundsPoints} request={fitRequest} padding={fitPadding}/>
            <FitMapBounds
                points={plannedRouteBoundsPoints}
                request={routeFitRequest}
                ignoreZeroRequest
                padding={fitPadding}
            />
            <FocusDriver position={selectedDriverLocation} request={driverFocusRequest}/>

            {routePaths.filter((route) => !plannedPickupIdSet.has(String(route.pickupId))).map((route) => (
                <PolylineF
                    key={`route-${route.pickupId}`}
                    path={route.path}
                    options={{
                        strokeColor: getDeliveryRouteColor(route.pickupId),
                        strokeOpacity: 0.9,
                        strokeWeight: 5,
                        zIndex: 3,
                    }}
                />
            ))}

            {selectedPlannedRouteStops.length > 0 && (
                <PlannedRoadRoute
                    startPosition={selectedDriverLocation}
                    stops={selectedPlannedRouteStops}
                    selectedDriverId={selectedDriverId}
                    onRouteMetrics={onRouteMetrics}
                />
            )}

            {trackingPaths.map((trackingPath) => (
                <PolylineF
                    key={`tracking-${trackingPath.pickupId}`}
                    path={trackingPath.path}
                    options={{
                        strokeColor: trackingPath.color,
                        strokeOpacity: 0,
                        strokeWeight: 3,
                        icons: [{
                            icon: {path: 'M 0,-1 0,1', strokeColor: trackingPath.color, strokeOpacity: 0.82, scale: 3},
                            offset: '0',
                            repeat: '14px',
                        }],
                        zIndex: 2,
                    }}
                />
            ))}

            {deliveryMarkerItems.map((item) => {
                const markerOffset = markerOffsets[item.key];
                const assigned = hasPickupDriver(item.pickup);
                const selected = selectedPickupIdSet.has(String(item.pickup.id));
                const plannedSequence = plannedStopSequenceByKey.get(
                    getPlannedStopKey(item.pickup.id, item.kind, item.position)
                );

                return (
                    <OverlayViewF
                        key={item.key}
                        position={item.position}
                        mapPaneName={OVERLAY_MOUSE_TARGET}
                        getPixelPositionOffset={getDeliveryMarkerOffset}
                        zIndex={getMarkerStackZIndex(selected ? 30 : 10, markerOffset)}
                    >
                        <span
                            className="dispatch-map-marker-spread"
                            data-testid={`marker-spread-${item.key}`}
                            style={getMarkerSpreadStyle(markerOffset)}
                        >
                            <button
                                type="button"
                                className={`dispatch-map-delivery-marker ${item.kind} ${assigned ? 'assigned' : 'unassigned'} ${selected ? 'selected' : ''}`}
                                style={{'--dispatch-route-color': getDeliveryRouteColor(item.pickup.id)}}
                                onClick={() => onSelectPickup(item.pickup.id)}
                                title={`${plannedSequence ? `Route stop ${plannedSequence} · ` : ''}${assigned ? 'Assigned' : 'Unassigned'} ${item.kind === 'origin' ? 'pickup' : 'drop-off'} for delivery #${item.pickup.id}`}
                                aria-label={`${selected ? 'Deselect' : 'Select'} ${assigned ? 'assigned' : 'unassigned'} delivery ${item.pickup.id} ${item.kind}`}
                            >
                                <span className="dispatch-map-delivery-pin">
                                    <span className="dispatch-map-delivery-label">{item.label}</span>
                                </span>
                                <span className="dispatch-map-delivery-status" aria-hidden="true">
                                    {assigned ? <Check/> : <Exclamation/>}
                                </span>
                            </button>
                        </span>
                    </OverlayViewF>
                );
            })}

            {driverMarkerItems.map((item) => {
                const markerOffset = markerOffsets[item.key];
                const selected = String(item.driver.id) === String(selectedDriverId);
                const fresh = isLocationFresh(item.location, now);
                const available = item.availability === 'available';
                const onDelivery = item.availability === 'busy';
                const truckType = getDriverTruckTypes(item.driver)[0];
                const truckTypeLabel = truckType?.title || truckType?.type || 'Vehicle not assigned';

                return (
                    <OverlayViewF
                        key={item.key}
                        position={item.position}
                        mapPaneName={OVERLAY_MOUSE_TARGET}
                        getPixelPositionOffset={driverOffset}
                        zIndex={getMarkerStackZIndex(selected ? 40 : 20, markerOffset)}
                    >
                        <span
                            className="dispatch-map-marker-spread"
                            data-testid={`marker-spread-${item.key}`}
                            style={getMarkerSpreadStyle(markerOffset)}
                        >
                            <button
                                type="button"
                                className={`dispatch-map-driver-marker ${onDelivery ? 'on-delivery' : 'no-active-delivery'} ${fresh ? 'live' : 'stale'} ${isDriverOnline(driverPresence[item.driver.id], now) ? 'online' : 'offline'} ${selected ? 'selected' : ''}`}
                                onClick={() => onSelectDriver(item.driver.id)}
                                title={`${item.driver.name || `Driver #${item.driver.id}`} · ${truckTypeLabel} · ${getDriverConnectionLabel(driverPresence[item.driver.id], now)} · ${fresh ? 'Fresh GPS' : 'Stale location'} · ${getDriverLocationLabel(item.location, now)} · ${onDelivery ? 'On delivery' : available ? 'Available' : 'Not available'}`}
                                aria-label={`Select driver ${item.driver.name || item.driver.id}`}
                            >
                                <span className="dispatch-driver-pulse"/>
                                <span className="dispatch-truck-marker-label">
                                    <TruckTypeIcon truckType={truckType} className="map-marker"/>
                                    <span className="dispatch-map-driver-avatar">
                                        {getDriverInitials(item.driver.name)}
                                        {item.driver.profile_photo && (
                                            <img
                                                key={item.driver.profile_photo}
                                                className="dispatch-map-driver-photo"
                                                src={item.driver.profile_photo}
                                                alt=""
                                                onError={(event) => {
                                                    event.currentTarget.hidden = true;
                                                }}
                                            />
                                        )}
                                    </span>
                                </span>
                                <span className="dispatch-driver-delivery-state" aria-hidden="true">
                                    {onDelivery ? <BoxSeam/> : <span className="dispatch-no-active-delivery-dash"/>}
                                </span>
                            </button>
                        </span>
                    </OverlayViewF>
                );
            })}

        </GoogleMap>
    );
};

export default DispatchMapCanvas;
